import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { FormattedMessage, useIntl } from 'react-intl';
import { Model } from 'pages/Benchmarking/performance/model';
import { useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useAppStore } from 'hooks/state/useAppStore';
import { TbIcon } from 'components/Figma';

interface Props {
  performance: Model;
  type: 'yoy' | 'mom';
  benchmark: string;
  isMoM: boolean;
  testIdPrefix?: string;
}

function Bold(chunks: React.ReactNode) {
  return <strong>{chunks}</strong>;
}

function Highlight(chunks: React.ReactNode) {
  return <span className="text-turquoise-300 font-bold">{chunks}</span>;
}

function PeerName(chunks: React.ReactNode) {
  return <span className="text-darkBlue-600">{chunks}</span>;
}

export function MetricsConclusionMessage({ performance, type, benchmark, isMoM, testIdPrefix = '' }: Props) {
  const { name } = useContext(TenantConfigContext);
  const intl = useIntl();

  const { benchmarkingDateRange } = useAppStore();

  const lastMonthPerformance = performance.timeSeries[performance.timeSeries.length - 1];
  const tenantValue = isMoM ? lastMonthPerformance.tenantValue : performance.tenantValue;
  const tenantCompareValue = isMoM ? lastMonthPerformance.tenantCompareValue : performance.tenantCompareValue;
  const benchmarkValue = isMoM ? lastMonthPerformance.benchmarkValue : performance.benchmarkValue;
  const benchmarkCompareValue = isMoM ? lastMonthPerformance.benchmarkCompareValue : performance.benchmarkCompareValue;

  const tenantGrowth = tenantValue / (tenantCompareValue ?? 1) - 1;
  const benchmarkGrowth = benchmarkValue / (benchmarkCompareValue ?? 1) - 1;

  const gap = tenantValue / ((tenantCompareValue ?? 0) * (1 + benchmarkGrowth) || 1) - 1;

  const revenueGap = Math.abs(tenantValue - benchmarkValue);

  const titleId = 'benchmarking.performance.summary.title';
  const firstLineId =
    type === 'yoy'
      ? 'benchmarking.performance.summary.yoy.text'
      : `benchmarking.performance.summary.text.${benchmarkingDateRange.temporalType.toLowerCase()}`;
  const secondLineId = 'benchmarking.performance.summary.market_gap.text';

  const tenantGrowthElement = (
    <span data-testid={`${testIdPrefix}-tenant`}>
      <MeasureFormat value={tenantGrowth} unit={MeasureUnitCode.PERCENT} config={{ hasSign: true }} />
    </span>
  );

  const benchmarkGrowthElement = (
    <span data-testid={`${testIdPrefix}-peer`}>
      <MeasureFormat value={benchmarkGrowth} unit={MeasureUnitCode.PERCENT} config={{ hasSign: true }} />
    </span>
  );

  const performanceGapElement = (
    <span data-testid={`${testIdPrefix}-gap`}>
      <MeasureFormat value={gap} unit={MeasureUnitCode.PERCENT} config={{ hasSign: true }} />
    </span>
  );

  return (
    <div className="w-full text-base text-gray-800 space-y-2 px-4 py-2">
      <div className="pb-1 border-b border-gray-200 flex items-center gap-2 font-semibold text-gray-800">
        <TbIcon name="List" className="text-gray-600" />
        <span>{intl.formatMessage({ id: titleId })}</span>
      </div>

      <p className="flex gap-1">
        <FormattedMessage
          id={firstLineId}
          values={{
            metric: intl.formatMessage({
              id: 'insights.measure.omsOrdDmvVal.name',
            }),
            direction: intl.formatMessage({
              id: tenantGrowth >= 0 ? 'benchmarking.performance.grew' : 'benchmarking.performance.declined',
            }),
            value: tenantGrowthElement,
            peerLabel: intl.formatMessage({
              id: `benchmarking.benchmarks.${benchmark}`,
            }),
            peerName: PeerName,
            peerDirection: intl.formatMessage({
              id:
                benchmarkGrowth >= 0
                  ? 'benchmarking.performance.grew.plural'
                  : 'benchmarking.performance.declined.plural',
            }),
            peerValue: benchmarkGrowthElement,
            b: Bold,
          }}
        />
      </p>

      <p className="flex gap-1">
        <FormattedMessage
          id={secondLineId}
          values={{
            tenant: name,
            performance: intl.formatMessage({
              id:
                gap > 0
                  ? 'benchmarking.performance.summary.over.performed'
                  : 'benchmarking.performance.summary.under.performed',
            }),
            gap: performanceGapElement,
            revenue: <MeasureFormat value={revenueGap} unit={MeasureUnitCode.CURRENCY} />,
            revenueText: intl.formatMessage({
              id: gap > 0 ? 'benchmarking.performance.revenue.additional' : 'benchmarking.performance.revenue.missing',
            }),
            revenueSuffix: intl.formatMessage({
              id:
                gap > 0
                  ? 'benchmarking.performance.revenue.additional.suffix'
                  : 'benchmarking.performance.revenue.missing.suffix',
            }),
            high: Highlight,
            b: Bold,
          }}
        />
      </p>
    </div>
  );
}
